/* eslint-disable react-refresh/only-export-components */
import { createContext, useContext, useEffect, useState, ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from '@atrvd/saas-ui'
import type { Organization } from '@/lib/supabase'
import { supabase } from '@/lib/supabase'
import { useProfile, useUpdateActiveOrganization } from '@/hooks/useProfile'
import { useAccessibleOrganizations } from '@/hooks/useAccessibleOrganizations'
import { UNIFIED_TEAM_INVITATIONS_KEYS } from '@/hooks/useOrganizationInvitations'

interface OrganizationContextValue {
  /** Currently selected organization */
  currentOrganization: Organization | null

  /** List of organizations the user can access */
  accessibleOrganizations: Organization[]

  /** Switch to a different organization (saves to DB) */
  setCurrentOrganization: (org: Organization) => void

  /** Whether the user can switch between organizations */
  canSwitchOrganizations: boolean

  /** Whether organizations are being loaded */
  isLoadingOrganizations: boolean
}

const OrganizationContext = createContext<OrganizationContextValue | null>(null)

interface OrganizationProviderProps {
  children: ReactNode
}

/**
 * Organization context provider
 *
 * Manages current organization selection. Since DCP-350 the ability to switch
 * follows the DATA, not the role: anyone who can reach more than one
 * organization gets the switcher, because a team member can now be invited into
 * a second organization. The selection is saved to
 * `profiles.active_organization_id` for everybody (the CHECK that restricted
 * that column to platform staff was dropped in the same change).
 *
 * A user with exactly one accessible organization is pinned to it and sees no
 * switcher — which is every single-organization user, i.e. unchanged behaviour.
 */
export function OrganizationProvider({ children }: OrganizationProviderProps) {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { data: profile, isLoading: isLoadingProfile } = useProfile()
  const { data: accessibleOrgs = [], isLoading: isLoadingOrgs } = useAccessibleOrganizations(
    profile?.role,
    profile?.organizationId
  )
  const updateActiveOrg = useUpdateActiveOrganization()

  const [currentOrganization, setCurrentOrganizationState] = useState<Organization | null>(null)

  // DCP-350: "may switch" = "has somewhere to switch to". `accessibleOrgs` comes
  // straight from the organizations RLS policy, so this cannot drift from what
  // the server would actually allow. Platform staff keep the switcher for the
  // same reason they always had it — they can reach many organizations.
  const canSwitch = accessibleOrgs.length > 1
  const isLoading = isLoadingProfile || isLoadingOrgs

  /**
   * Check if organization is suspended and sign out non-super_admin users
   * This is a fallback for edge cases (e.g., organization suspended while user is logged in)
   */
  useEffect(() => {
    if (!currentOrganization || !profile) {
      return
    }

    // super_admin can access suspended organizations
    if (profile.role === 'super_admin') {
      return
    }

    // Sign out if organization is suspended
    if (currentOrganization.status === 'suspended') {
      supabase?.auth
        .signOut()
        .then(() => {
          toast.error('Your organization account has been suspended. Please contact support.')
          navigate('/login')
        })
        .catch((error) => {
          console.error('Failed to sign out suspended user:', error)
          toast.error('Your organization is suspended. Please refresh the page.')
          navigate('/login')
        })
    }
  }, [currentOrganization, profile, navigate])

  /**
   * Initialize current organization from profile or accessible list.
   * Uses functional setState to skip updates when org ID hasn't changed,
   * preventing unnecessary re-renders across all context consumers.
   */
  useEffect(() => {
    if (isLoading || !profile || accessibleOrgs.length === 0) {
      return
    }

    // Preference order: the organization the user last chose, then their HOME
    // organization, then whatever is first in the list.
    //
    // The home step is not decoration (DCP-350). Before multi-org membership an
    // ordinary user had exactly one accessible organization, so "first in the
    // list" was always theirs. Now someone invited into a second organization
    // has two, usually with no `active_organization_id` yet — the column was
    // unwritable for them until this release — and `accessibleOrgs` is sorted
    // by NAME. Falling straight through to `[0]` would open the guest
    // organization for anyone whose host is alphabetically earlier, so a
    // treasury or accounts-payable user would land on somebody else's banking
    // data before touching the switcher.
    let targetOrg: Organization | undefined

    if (profile.activeOrganizationId) {
      targetOrg = accessibleOrgs.find((org) => org.id === profile.activeOrganizationId)
    }

    if (!targetOrg && profile.organizationId) {
      targetOrg = accessibleOrgs.find((org) => org.id === profile.organizationId)
    }

    // Platform staff have no home organization, and a stale
    // active_organization_id can point at an organization no longer accessible.
    if (!targetOrg) {
      targetOrg = accessibleOrgs[0]
    }

    // Functional update: only change state when org ID differs.
    // Avoids identity-churn re-renders when profile refetches return
    // a new object reference with the same org ID.
    const org = targetOrg
    setCurrentOrganizationState(prev => (prev?.id === org.id ? prev : org))
  }, [profile, accessibleOrgs, isLoading])

  /**
   * Switch organization and save to database
   * Invalidates all organization-scoped queries to refetch data for new organization
   * Rolls back to previous organization if persistence fails
   */
  const setCurrentOrganization = (org: Organization) => {
    if (!canSwitch) {
      console.warn('User does not have permission to switch organizations')
      return
    }

    // Store previous organization for rollback on error
    const previousOrg = currentOrganization

    // Update local state immediately for responsive UI (optimistic update).
    // Tenant caches are keyed by orgId — new org gets its own cache entry,
    // old org's cached client stays valid for potential rollback.
    setCurrentOrganizationState(org)

    // Invalidate all organization-scoped queries
    // This ensures all data is refetched for the new organization
    queryClient.invalidateQueries({ queryKey: ['profile'] })
    queryClient.invalidateQueries({ queryKey: ['banking'] })
    queryClient.invalidateQueries({ queryKey: ['plaid'] })
    queryClient.invalidateQueries({ queryKey: ['facilities'] })
    queryClient.invalidateQueries({ queryKey: ['team-members'] })
    queryClient.invalidateQueries({ queryKey: UNIFIED_TEAM_INVITATIONS_KEYS.all })
    queryClient.invalidateQueries({ queryKey: ['categories'] })
    queryClient.invalidateQueries({ queryKey: ['projections'] })

    // Persist to database
    updateActiveOrg.mutate(org.id, {
      onError: (error) => {
        console.error('Failed to persist organization switch:', error)
        // Rollback to previous organization on error
        if (previousOrg) {
          setCurrentOrganizationState(previousOrg)
          // Re-invalidate queries to refetch data for the rolled-back organization
          queryClient.invalidateQueries({ queryKey: ['profile'] })
          queryClient.invalidateQueries({ queryKey: ['banking'] })
          queryClient.invalidateQueries({ queryKey: ['plaid'] })
          queryClient.invalidateQueries({ queryKey: ['facilities'] })
          queryClient.invalidateQueries({ queryKey: ['team-members'] })
          queryClient.invalidateQueries({ queryKey: UNIFIED_TEAM_INVITATIONS_KEYS.all })
          queryClient.invalidateQueries({ queryKey: ['categories'] })
          queryClient.invalidateQueries({ queryKey: ['projections'] })
        }
        toast.error('Failed to switch organization. Reverted to previous selection.')
      },
    })
  }

  const value: OrganizationContextValue = {
    currentOrganization,
    accessibleOrganizations: accessibleOrgs,
    setCurrentOrganization,
    canSwitchOrganizations: canSwitch,
    isLoadingOrganizations: isLoading,
  }

  return (
    <OrganizationContext.Provider value={value}>
      {children}
    </OrganizationContext.Provider>
  )
}

/**
 * Hook to access organization context
 */
export function useOrganization() {
  const context = useContext(OrganizationContext)

  if (!context) {
    throw new Error('useOrganization must be used within OrganizationProvider')
  }

  return context
}